import { FunctionComponent, useContext, useState } from 'react'
import styled, { css, useTheme } from 'styled-components'
import { useDropzone } from 'react-dropzone'
import { getFileValidationErrorMessage } from 'src/utils/getFileValidationErrorMessage'
import { flow } from 'lodash'

import { Spacing } from 'src/resources/layout'
import { FlatButton } from 'src/resources/elements/buttons/FlatButton'
import Notification from 'src/resources/elements/Notification'
import { DefaultTheme, themed } from 'src/applications/Embed/fragments/ThemeWrapper'
import { useTranslation } from 'react-i18next'
import { FeatureFlagContext } from 'src/applications/Embed/fragments/FeatureFlagContext'

const OrBlock = styled.div`
  position: relative;
  border-bottom: 1px solid ${themed('borderColor')};
  width: 100%;
  margin-top: 20px;
  margin-bottom: 29px;

  span {
    position: absolute;
    width: 52px;
    top: -10px;
    left: calc(50% - 26px);
    background-color: ${themed('modalBackgroundColor')};
    display: block;
    font-size: ${themed('contentSmallFontSize')};
    font-weight: 500;
    line-height: 19px;
    text-align: center;
    color: ${themed('textColor')};
  }

  ${({ theme }) =>
    theme.invertedFileUpload &&
    css`
      span {
        background-color: ${themed('sidebarBackground')};
      }
    `}
`

const Overlay = styled.div<{ isDragActive?: boolean; primaryColor?: string }>`
  height: 100%;
`

const DropzoneSpace = styled.div<{
  isDragActive?: boolean
  primaryColor?: string
  hideDropFile?: boolean
  theme?: any
}>`
  border: ${themed('borderWidth')} dashed
    ${({ isDragActive }) => (isDragActive ? themed('primary') : themed('borderColor'))};
  border-radius: ${themed('borderRadius')};
  display: flex;
  justify-content: center;
  width: 100%;
  height: calc(100% - 80px);
  min-height: 400px;
  &:focus {
    outline: none;
  }

  h2,
  p {
    white-space: nowrap;
    text-align: center;
  }

  h2 {
    font-size: 24px;
    font-weight: 500;
    line-height: 24px;
    color: ${themed('textColor')};
    margin-bottom: 32px;
    & ~ div {
      display: flex;

      & > img:first-child {
        margin-right: 10px;
      }
    }
  }

  p {
    font-size: ${themed('contentSmallFontSize')};
    line-height: 19px;
    letter-spacing: 0;
    color: ${themed('textColor')};
    margin-top: 32px;
    margin-bottom: 24px;
    opacity: 0.8;
  }

  button {
    margin-top: ${Spacing.basePadding2x};
    text-align: center;
    cursor: pointer;
  }

  & > div {
    min-height: 400px;
    max-width: 300px;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  ${({ theme }) =>
    theme.invertedFileUpload &&
    css`
      background-color: ${themed('sidebarBackground')};
      border: none;
    `};

  // For embedded demos
  ${({ hideDropFile }) =>
    hideDropFile &&
    css`
      border: none;
      height: unset;
      min-height: 300px;
      & > div {
        min-height: 300px;
      }
    `}
`

Overlay.defaultProps = {
  theme: {
    primary: 'black'
  }
}

interface FileUploaderProps {
  onDrop: (f: File) => void
}

export const FileUploader: FunctionComponent<FileUploaderProps> = ({ onDrop, children }) => {
  // this flag is only used for embedded apps ie: Retail marketing demo
  // parent / child iframe relationship cannot share drop events
  // ** do not use elsewhere **
  const features = useContext(FeatureFlagContext)
  const hideDropFile = features?.includes('HIDE_DROP_FILE')
  const [errorBannerMessage, setErrorBannerMessage] = useState<string>()
  const displayErrorMessage = flow(getFileValidationErrorMessage, setErrorBannerMessage)
  const hideErrorMessage = () => setErrorBannerMessage('')
  const { getRootProps, open, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: '.csv,.xls,.xlsx,.txt,.gz',
    noClick: true,
    noKeyboard: true,
    maxSize: 1000000000,
    onDropRejected: (fileRejections) => displayErrorMessage(fileRejections, theme),
    onDrop: ([file]) => {
      if (file) {
        hideErrorMessage()
        onDrop(file)
      }
    }
  })
  const { t } = useTranslation()

  const theme = useTheme() as DefaultTheme

  return (
    <Overlay data-testid='drag-overlay' isDragActive={isDragActive} {...getRootProps()}>
      {errorBannerMessage && (
        <Notification
          fullWidth
          type='warning'
          title={errorBannerMessage}
          marginBottom={Spacing.basePadding3x}
        />
      )}
      <input id='file-inputid' data-testid='file-input' {...getInputProps()} />
      <DropzoneSpace isDragActive={isDragActive} hideDropFile={hideDropFile}>
        <div>
          {!hideDropFile && <h2>{t(theme?.textOverrides?.dragAndDropHint)}</h2>}
          <div>{theme?.iconOverrides_Upload}</div>
          <p>{t(theme?.textOverrides?.fileTypeHint)}</p>
          {!hideDropFile && (
            <OrBlock>
              <span>{t('components.FileUploader.orBlock')}</span>
            </OrBlock>
          )}
          <FlatButton color='primary' onClick={() => open()}>
            {t('elements.buttons.selectFile')}
          </FlatButton>
        </div>
      </DropzoneSpace>
      {children}
    </Overlay>
  )
}
